import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Card, DatePicker, Form, Input, Select, Space, Spin, message,
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  useFleetOperator,
  usePortalPermissions,
  type FleetOperator,
} from '@ubus/shared';
import AdminSectionPage from '../components/AdminSectionPage';
import ForbiddenPage from './ForbiddenPage';

function FleetProfile() {
  const { t } = useTranslation();
  const { operator, operatorId, loading: operatorLoading } = useFleetOperator();
  const { hasPermission, loading: permLoading } = usePortalPermissions();
  const canView = hasPermission('fleet_profile.view');
  const canEdit = hasPermission('fleet_profile.edit');
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [record, setRecord] = useState<FleetOperator | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!operatorId) {
      setRecord(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('fleet_operators')
        .select('*')
        .eq('id', operatorId)
        .maybeSingle();
      if (error) throw error;
      const row = (data as FleetOperator | null) ?? null;
      setRecord(row);
      if (row) {
        form.setFieldsValue({
          name: row.name,
          contact_person: row.contact_person,
          contact_phone: row.contact_phone,
          contact_email: row.contact_email,
          address: row.address ?? '',
          status: row.status,
          comprehensive_insurance_expiry: row.comprehensive_insurance_expiry
            ? dayjs(row.comprehensive_insurance_expiry)
            : null,
          public_liability_expiry: row.public_liability_expiry
            ? dayjs(row.public_liability_expiry)
            : null,
        });
      }
    } catch (err) {
      console.error(err);
      message.error(t('fleetProfile.loadFailed'));
      setRecord(null);
    } finally {
      setLoading(false);
    }
  }, [operatorId, form, t]);

  useEffect(() => {
    if (!operatorLoading) void load();
  }, [operatorLoading, load]);

  const handleSubmit = async (values: Record<string, unknown>) => {
    if (!operatorId || !canEdit) return;
    setSaving(true);
    try {
      const payload = {
        name: String(values.name ?? '').trim(),
        contact_person: String(values.contact_person ?? '').trim(),
        contact_phone: String(values.contact_phone ?? '').trim(),
        address: String(values.address ?? '').trim() || null,
        status: values.status as 'active' | 'inactive',
        comprehensive_insurance_expiry: values.comprehensive_insurance_expiry
          ? dayjs(values.comprehensive_insurance_expiry as dayjs.Dayjs).format('YYYY-MM-DD')
          : null,
        public_liability_expiry: values.public_liability_expiry
          ? dayjs(values.public_liability_expiry as dayjs.Dayjs).format('YYYY-MM-DD')
          : null,
        updated_at: new Date().toISOString(),
      };
      const { error } = await supabase
        .from('fleet_operators')
        .update(payload)
        .eq('id', operatorId);
      if (error) throw error;
      message.success(t('fleetProfile.saved'));
      await load();
    } catch (err) {
      console.error(err);
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : err instanceof Error
            ? err.message
            : String(err);
      if (msg.includes('LOGIN_FIELDS_READONLY')) {
        message.error(t('fleetProfile.loginFieldsReadonly'));
      } else {
        message.error(msg || t('fleetProfile.saveFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  if (permLoading || operatorLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!canView) {
    return <ForbiddenPage />;
  }

  return (
    <AdminSectionPage
      title={t('fleetProfile.title')}
      subtitle={t('fleetProfile.subtitle')}
      loading={loading}
    >
      {!operator && !loading ? (
        <Alert type="warning" showIcon message={t('fleetProfile.noOperator')} />
      ) : (
        <Card style={{ maxWidth: 640 }}>
          {!canEdit && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('fleetProfile.readOnlyHint')}
            />
          )}
          <Form
            form={form}
            layout="vertical"
            disabled={!canEdit || !record}
            onFinish={(v) => void handleSubmit(v)}
          >
            <Form.Item
              name="name"
              label={t('fleetOperators.fields.companyName')}
              rules={[{ required: true, message: t('fleetProfile.required') }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="contact_person"
              label={t('fleetOperators.fields.contactPerson')}
              rules={[{ required: true, message: t('fleetProfile.required') }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="contact_phone"
              label={t('fleetOperators.fields.phone')}
              rules={[{ required: true, message: t('fleetProfile.required') }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="contact_email"
              label={t('fleetOperators.fields.emailLogin')}
              extra={t('fleetProfile.emailReadonlyHint')}
            >
              <Input disabled />
            </Form.Item>
            <Form.Item name="address" label={t('fleetOperators.fields.address')}>
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item
              name="comprehensive_insurance_expiry"
              label={t('fleetOperators.fields.comprehensiveInsuranceExpiry')}
            >
              <DatePicker style={{ width: '100%' }} allowClear />
            </Form.Item>
            <Form.Item
              name="public_liability_expiry"
              label={t('fleetOperators.fields.publicLiabilityExpiry')}
            >
              <DatePicker style={{ width: '100%' }} allowClear />
            </Form.Item>
            <Form.Item
              name="status"
              label={t('fleetOperators.fields.status')}
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { value: 'active', label: t('fleetOperators.status.active') },
                  { value: 'inactive', label: t('fleetOperators.status.inactive') },
                ]}
              />
            </Form.Item>
            {canEdit && (
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={saving}>
                    {t('fleetProfile.save')}
                  </Button>
                  <Button onClick={() => void load()} disabled={saving}>
                    {t('fleetProfile.reload')}
                  </Button>
                </Space>
              </Form.Item>
            )}
          </Form>
        </Card>
      )}
    </AdminSectionPage>
  );
}

export default FleetProfile;
